import { ModalRouteService } from './../../../services/modal-route.service';
import { ThumbnailPipe } from './../../../pipes/thumbnail.pipe';
import { PublicationService } from './../../../services/publication.service';
import { fromEvent as observableFromEvent, Subject, Subscription } from 'rxjs';
import {
	Component, Input, ViewChild, SimpleChanges, ViewContainerRef, ChangeDetectorRef, ViewChildren, ElementRef,
	QueryList, HostBinding, OnChanges, AfterViewInit, OnDestroy, OnInit
} from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';

import { FilterConfig } from './../../../services/filter-set.service';
import { MenuService } from '../../../services/menu.service';
import { OverlayService } from '../../../services/overlay.service';
import { debounceTime } from 'rxjs/operators';
import { CdkOverlayOrigin, OverlayRef } from '@angular/cdk/overlay';
import { BbMenuPanelComponent } from '../bb-menu-panel/bb-menu-panel.component';

export interface TabItem {
	title: string;
	routerLink: string[];
	icon: string;
	internalRouterLink?: string[];
	modalRouterLink?: any;
	hide?: boolean;
	right?: number;
	root?: boolean;
	active?: boolean;
	activeClass?: string;
}

@Component({
	selector: 'bb-page-header',
	templateUrl: './bb-page-header.component.html',
	styleUrls: ['./bb-page-header.component.scss']
})

export class BbPageHeaderComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

	@ViewChildren('tabs') tabs: QueryList<ElementRef>;

	@ViewChild('dropdown') dropDownOrigin: CdkOverlayOrigin;
	@HostBinding('style.background-image') backgroundImageUrl: string;

	@Input() title: string;
	@Input() filename: string;

	@Input() icon: string;
	@Input() backOnMobile: any;

	@Input() theme: string;

	// Forward to bb-filters component
	@Input() filterConfig: FilterConfig;
	@Input() currentPath: string;
	@Input() search = false;
	@Input() allowCustomPreset: boolean;
	@Input() presetToggable = false;

	@Input() tabItems: TabItem[] = [];

	@Input() inModal: boolean;

	activeId: string;
	activeTab: string;
	activeTabInList: boolean;
	verticalList: TabItem[];

	lastMinWidth: number;
	faIcon = 'fa';

	overlayRef: OverlayRef;

	public tabsVisibleMobile = false;

	backdropClickSubject: Subject<void>;
	subscriptions = new Subscription();

	constructor(
		private router: Router,
		private menuService: MenuService,
		private route: ActivatedRoute,
		private overlayService: OverlayService,
		private changeDetector: ChangeDetectorRef,
		public viewContainerRef: ViewContainerRef,
		private publicationService: PublicationService,
		private modalRouteService: ModalRouteService,
		private thumbnailPipe: ThumbnailPipe
	) {

		this.backdropClickSubject = new Subject<void>();

		this.subscriptions.add(this.route.params.subscribe((params: Params) => {
			if (params) {
				const lastActiveId = this.activeId;
				this.activeId = params.id;
				if (this.activeTab !== params.tab) {
					this.closeMore();
					this.activeTab = params.tab;
					this.dropdownIsActive();
					this.updateActiveTab();
				}
				if (lastActiveId !== params.id) {
					this.updateTabRouterLinks();
				}
				this.updateActiveTab();
			}
		}));

		this.subscriptions.add(this.publicationService.getData().subscribe((pub) => {
			if (pub && pub.whitelabelHeaderImageId) {
				this.thumbnailPipe.transform({ id: pub.whitelabelHeaderImageId }, 'default', 'default', 'backgroundUrl').then((imageUrl) => {
					this.backgroundImageUrl = imageUrl;
				});
			}
		}));

		this.subscriptions.add(observableFromEvent(window, 'resize').pipe(
			debounceTime(10)
		).subscribe(() => {
			this.checkTabsMaxWidth();
		}));
	}

	ngOnInit() {
		if (this.icon === 'facebook' || this.icon === 'youtube' || this.icon === 'instagram') {
			this.faIcon = 'fa-brands';
		}
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges && (simpleChanges.tabItems || simpleChanges.entityConfiguration)) {
			this.updateTabRouterLinks();
			this.updateActiveTab();
		}
	}

	ngAfterViewInit() {
		this.subscriptions.add(this.tabs.changes.subscribe(() => {
			if (this.tabs.last) {
				this.checkTabsMaxWidth();
			}
		}));
		this.checkTabsMaxWidth();
	}

	checkTabsMaxWidth() {
		if (this.tabs && this.tabs.last) {
			this.closeMore();
			const clientWidth = this.tabs.last.nativeElement.clientWidth;
			if (this.lastMinWidth === clientWidth) {
				// return; // disabled this to fix initially not showing dropdown
			}
			if (this.tabItems && this.tabItems.length && !this.tabItems[0].right) {
				const nativeElement: HTMLElement = this.tabs.last.nativeElement;
				for (let i = this.tabItems.length - 1; i >= 0; i--) {
					const child = nativeElement.children[i];
					if (child) {
						if (i === 0 && this.tabItems.length > 1) {
							if (child.getBoundingClientRect().right === nativeElement.children[1].getBoundingClientRect().right) {
								break;
							}
						}
						this.tabItems[i].right = child.getBoundingClientRect().right;
					}
				}
			}
			if (this.tabs.last.nativeElement.scrollWidth < 750) {
				if (this.verticalList && this.verticalList.length > 0) {
					this.verticalList = [];
				}
				this.tabItems.forEach((tabItem: TabItem) => {
					tabItem.hide = false;
				});
				this.lastMinWidth = clientWidth;
			} else if (this.tabs.last.nativeElement.scrollWidth > clientWidth) {
				const verticalList: TabItem[] = [];

				for (let i = this.tabItems.length - 1; i >= 0; i--) {
					let offset = 10;
					if (verticalList && verticalList.length > 0) {
						offset = 50;
					}
					if (this.tabItems[i] && this.tabItems[i].right > (clientWidth - offset)) {
						this.tabItems[i].hide = true;
						verticalList.unshift(this.tabItems[i]);
						continue;
					}
					break;
				}

				this.verticalList = verticalList;
				this.lastMinWidth = clientWidth;
			} else if (clientWidth > this.lastMinWidth) {
				const verticalList: TabItem[] = [];
				this.tabItems.forEach((tabItem: TabItem) => {
					if (tabItem.right < clientWidth) {
						tabItem.hide = false;
					} else {
						tabItem.hide = true;
						verticalList.push(tabItem);
					}
				});

				this.verticalList = verticalList;
				this.lastMinWidth = clientWidth;
			}
			this.dropdownIsActive();
		}
	}

	updateActiveTab() {
		if (this.tabItems && this.tabItems.length) {
			const tabItem = this.tabItems[0];
			const activeTabItem = tabItem.routerLink[tabItem.routerLink.length - 1];
			tabItem.active = this.activeTab === activeTabItem;
		}
	}

	updateTabRouterLinks() {
		if (this.tabItems) {
			this.tabItems.forEach((tabItem: TabItem) => {
				const routerLink: string[] = [...tabItem.routerLink];
				const begin = routerLink.shift();
				if (this.activeId) {
					routerLink.unshift(this.activeId);
				}
				routerLink.unshift(begin);
				tabItem.internalRouterLink = routerLink;
				tabItem.modalRouterLink = this.modalRouteService.convertToModalRoute(routerLink);
				tabItem.activeClass = 'active';
				tabItem.hide = false;
			});
			if (this.tabItems.length) {
				const firstTabItem = this.tabItems[0];
				firstTabItem.activeClass = '';
			}
		}
	}

	dropdownIsActive() {
		let activeTabInList = false;
		if (this.verticalList) {
			for (let i = 0; i < this.verticalList.length; i++) {
				if (this.verticalList[i] && this.verticalList[i].routerLink) {
					const routerLinkSize = this.verticalList[i].routerLink.length;
					if (this.activeTab === this.verticalList[i].routerLink[routerLinkSize - 1]) {
						activeTabInList = true;
						break;
					}
				}
			}
			this.activeTabInList = activeTabInList;
			this.changeDetector.detectChanges();
		} else {
			this.activeTabInList = activeTabInList;
		}
	}

	showMore() {
		this.overlayRef = this.overlayService.createConnectedOverlayComponent(
			BbMenuPanelComponent,
			this.dropDownOrigin.elementRef,
			this.viewContainerRef,
			{
				tabItems: this.verticalList
			},
			[
				{
					function: this.backdropClickSubject.next,
					this: this.backdropClickSubject
				},
				{
					function: () => {
						this.closeMore();
					},
					this: this
				}
			],
			[
				{
					originX: 'end',
					originY: 'bottom',
					overlayX: 'end',
					overlayY: 'top'
				}
			]
		);

		this.changeDetector.reattach();
	}

	closeMore() {
		if (this.overlayRef) {
			this.changeDetector.detach();
			this.overlayRef.dispose();
			this.overlayRef = null;
		}
	}

	goBack() {
		this.router.navigate(this.backOnMobile);
	}

	showMobileMenu() {
		this.menuService.showMobileMenu();
	}
}
